import { useAuth, useSignIn, useUser } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FullPageSpinner } from '../components/FullPageSpinner.js';
import { trackError, trackEvent } from '../lib/telemetry.js';

/**
 * Arriving from the app's "Open the portal", already signed in (2026-10-20).
 *
 * The app asks the API for a one-time, sixty-second ticket for the person using it and opens
 * `/handoff#ticket=…`. Rasel: "they're already logged into the app... We don't want them to have
 * to log in again."
 *
 * - **In the fragment, not the query string.** A fragment never reaches the server, a CDN log or a
 *   Referer header. It is still cleared from the address bar before anything else happens.
 * - **Somebody already signed in here is asked, not silently replaced.** Otherwise anyone able to
 *   mint a ticket could send a link that quietly swaps a person into a different account.
 * - **A full page load after sign-in**, the same way the email link arrives, so the Fig session is
 *   exchanged before anything decides where to send them. A client-side navigate raced it and
 *   landed people on the sign-in screen while signed in.
 *
 * Any failure (an expired or used ticket, a network error) lands on the ordinary sign-in screen
 * with one line saying why. They still get the portal; only the shortcut failed.
 */
export function Handoff() {
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: authLoaded, isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  // StrictMode runs effects twice in development, and a ticket can be used exactly once.
  const started = useRef(false);
  const ticket = useRef<string | null>(null);
  const [askToSwitch, setAskToSwitch] = useState(false);

  useEffect(() => {
    if (!signInLoaded || !authLoaded || !signIn || started.current) return;
    started.current = true;
    ticket.current ??= new URLSearchParams(window.location.hash.slice(1)).get('ticket');
    if (!ticket.current) { navigate('/', { replace: true }); return; }

    if (isSignedIn) { setAskToSwitch(true); return; }

    // Out of the address bar before anything else happens.
    window.history.replaceState(null, '', '/handoff');
    (async () => {
      try {
        const attempt = await signIn.create({ strategy: 'ticket', ticket: ticket.current! });
        if (attempt.status !== 'complete' || !attempt.createdSessionId) throw new Error(`incomplete:${attempt.status ?? 'none'}`);
        await setActive({ session: attempt.createdSessionId });
        trackEvent('handoff.signed_in');
        window.location.replace('/');
      } catch (err) {
        // WHY IT FAILED, RECORDED (2026-10-21). This catch used to swallow everything, so two failed
        // hand-offs on 21 Oct left no trace at all: the API issued both tickets and then heard nothing.
        // Clerk's error code (never the ticket) goes to App Insights, and into the sign-in screen's URL
        // so the person sees one specific line instead of a generic apology.
        const code = (err as { errors?: { code?: string }[] })?.errors?.[0]?.code
          ?? ((err as Error)?.message?.startsWith('incomplete:') ? (err as Error).message : 'unknown');
        trackError(err, { where: 'handoff', clerkCode: code });
        // Already signed in after all (Clerk says so): the shortcut worked in every way that matters.
        if (code === 'session_exists') { window.location.replace('/'); return; }
        navigate(`/sign-in?handoff=failed&reason=${encodeURIComponent(code)}`, { replace: true });
      }
    })();
  }, [signInLoaded, authLoaded, signIn, isSignedIn, setActive, navigate]);

  if (askToSwitch) {
    const who = user?.primaryEmailAddress?.emailAddress ?? 'another account';
    return (
      <main className="page page-centred">
        <h1 className="page-title">Switch accounts?</h1>
        <p className="state-body">
          This browser is signed in as <strong>{who}</strong>. Opening the portal from the app will
          sign that account out here and sign you in as the person using the app.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            // Back to this same address, ticket and all: it is good for sixty seconds.
            onClick={() => { void signOut({ redirectUrl: window.location.href }); }}
          >
            Switch
          </button>
          <button
            className="btn"
            onClick={() => { window.history.replaceState(null, '', '/handoff'); navigate('/', { replace: true }); }}
          >
            Stay as {who}
          </button>
        </div>
      </main>
    );
  }

  return <FullPageSpinner label="Signing you in" />;
}
